// Permissions utility for role-based access control

/**
 * Check if user has a specific role
 */
export function hasRole(user, role) {
  if (!user) return false;
  return user.role === role;
}

/**
 * Check if user is Super Admin
 */
export function isSuperAdmin(user) {
  return hasRole(user, "super_admin");
}

/**
 * Check if user is Admin (any admin role)
 */
export function isAdmin(user) {
  return user?.role === "super_admin" || user?.role === "admin";
}

/**
 * Check if user has permission for a specific action
 */
/**
 * Check if user has permission for a specific action
 */
export function hasPermission(user, permission) {
  if (!user) return false;

  // Super Admin has all permissions (Robust Check)
  if (user.role === "super_admin") return true;
  if (user.role && user.role.slug === "super_admin") return true;
  if (user.user_role && user.user_role.slug === "super_admin") return true;
  if (user.userRole && user.userRole.slug === "super_admin") return true;

  // Check dynamic permissions array from backend
  if (user.permissions && Array.isArray(user.permissions)) {
    // Check for exact match
    if (user.permissions.includes(permission)) return true;

    // Check for wildcard match (e.g., 'product.*' matches 'product.view')
    const parts = permission.split(".");
    if (parts.length > 1) {
      const wildcard = parts[0] + ".*";
      if (user.permissions.includes(wildcard)) return true;
    }
  }

  return false;
}

/**
 * Check if user can perform CRUD operation on a resource
 */
export function canCreate(user, resource) {
  return hasPermission(user, `${resource}.create`);
}

export function canEdit(user, resource) {
  return hasPermission(user, `${resource}.edit`);
}

export function canDelete(user, resource) {
  return hasPermission(user, `${resource}.delete`);
}

export function canView(user, resource) {
  return hasPermission(user, `${resource}.view`);
}

/**
 * Get user from localStorage
 */
export function getCurrentUser() {
  try {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
}

/**
 * Get permissions from localStorage
 */
export function getUserPermissions() {
  try {
    const permStr = localStorage.getItem("permissions");
    return permStr ? JSON.parse(permStr) : [];
  } catch (e) {
    return [];
  }
}

/**
 * Check if sidebar item should be visible
 */
export function canViewSidebarItem(user, itemName) {
  if (!user) return false;

  // Reuse robust check
  if (hasPermission(user, "DEBUG_SUPER_ADMIN_CHECK")) return true; // Hack to reuse logic? No, just copy for safety or direct call if permission is dummy
  // Better:
  if (user.role === "super_admin") return true;
  if (user.role && user.role.slug === "super_admin") return true;
  if (user.user_role && user.user_role.slug === "super_admin") return true;
  if (user.userRole && user.userRole.slug === "super_admin") return true;

  // Map sidebar items to permission groups/slugs
  const mapping = {
    dashboard: "dashboard.view",
    orders: "orders.view",
    products: "products.view",
    categories: "categories.manage",
    brands: "brands.manage",
    tags: "tags.manage",
    users: "users.view",
    admins: "staff.manage",
    roles: "system.roles_manage",
    certificates: "orders.certifications",
    settings: "system.settings",
    "site-settings": "system.settings",
    menu: "system.menu",

    // Marketing
    blog: "marketing.blog_posts",
    banners: "marketing.banners",
    newsletter: "marketing.newsletter",
    homepage: "marketing.homepage",
    "blog-categories": "marketing.blog_categories",
    "marketing.coupons": "marketing.coupons",
    "marketing.promotions": "marketing.promotions",
    "marketing.blog_categories": "marketing.blog_categories",
    "marketing.blog_posts": "marketing.blog_posts",
    "marketing.banners": "marketing.banners",
    "marketing.homepage": "marketing.homepage",
    "marketing.newsletter": "marketing.newsletter",

    revenue: "dashboard.revenue",
    "payment-settings": "system.payment",
    permissions: "system.permissions_view",
    "staff.manage": "staff.manage",
    "product.inventory": "product.inventory",
    "warehouses.manage": "warehouses.manage",
    "system.roles_manage": "system.roles_manage",
    "system.permissions_view": "system.permissions_view",
    "system.settings": "system.settings",
    "system.payment": "system.payment",
    "system.menu": "system.menu",
    "dashboard.revenue": "dashboard.revenue",
    "orders.certifications": "orders.certifications",
    "chat.manage": "chat.manage",
    "users.view": "users.view",
  };

  const needed = mapping[itemName];
  // If explicitly mapped, check it. If not mapped (undefined), check if it's a direct permission string
  if (needed) {
    return hasPermission(user, needed);
  }

  // Fallback: assume itemName IS the permission slug if not in map
  return hasPermission(user, itemName);
}
